import { FC } from 'react'
import {
    Form,
    Input,
    Button,
    Select,
    Switch,
    Row,
    Col,
    Flex,
    TimePicker,
    InputNumber,
    Alert,
} from 'antd'
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { ApprovalStatus, ClientSource } from '../../../types/api/contractor'
import { BusinessRole } from '../../../types/api/user'
import { useAppSelector } from '../../../store/store'
import Phone from '../../../components/form-items/phone/phone'
import CuratorsField from '../../../components/form-items/curators/curators-field'
import { phoneRules, requiredRules } from '../../../constants/forms/validation/rules'
import AddressSuggest from '../../../components/form-items/address-suggest/address-suggest'

export interface ContactFormValues {
    documentId?: string
    address?: string
    noAddress?: boolean
    pointName?: string
    receiverName?: string
    phone?: string
    workingHours?: string | [Dayjs, Dayjs]
    latCoordinates?: number
    longCoordinates?: number
    comment?: string
    isPrimary?: boolean
}

export interface CuratorFormValues {
    user?: string
    percent?: number
}

export interface ClientFormValues {
    name: string
    approvalStatus?: ApprovalStatus
    legalAddress?: string
    noLegalAddress?: boolean
    defermentInDays?: number
    source?: ClientSource
    exhibitionName?: string
    comment?: string
    curators?: CuratorFormValues[]
    contacts?: ContactFormValues[]
}

interface ClientFormProps {
    initialValues?: ClientFormValues
    loading?: boolean
    onFinish: (values: ClientFormValues) => void
    submitText: string
    disabled?: boolean
}

const parseWorkingHours = (value: string | undefined): [Dayjs, Dayjs] | undefined => {
    if (!value || typeof value !== 'string') return undefined
    const parts = value.split('-')
    if (parts.length !== 2) return undefined

    const [start, end] = parts.map(part => part.trim())
    if (!start || !end) return undefined

    const startTime = dayjs(start, 'HH:mm')
    const endTime = dayjs(end, 'HH:mm')

    if (!startTime.isValid() || !endTime.isValid()) return undefined

    return [startTime, endTime]
}

const formatWorkingHours = (value: [Dayjs, Dayjs] | null | undefined): string | undefined => {
    if (!value || !Array.isArray(value) || value.length !== 2) return undefined
    const [start, end] = value
    if (!start || !end || !dayjs.isDayjs(start) || !dayjs.isDayjs(end)) return undefined
    return `${start.format('HH:mm')}-${end.format('HH:mm')}`
}

const ClientForm: FC<ClientFormProps> = ({
    initialValues,
    loading,
    onFinish,
    submitText,
    disabled,
}) => {
    const [form] = Form.useForm()
    const user = useAppSelector(state => state.auth.user)
    const isLogist = user?.businessRoles?.includes(BusinessRole.Logist)

    const normalizedInitialValues = initialValues
        ? {
              ...initialValues,
              defermentInDays: initialValues.defermentInDays ?? 0,
              noLegalAddress: initialValues.noLegalAddress ?? !initialValues.legalAddress?.trim(),
              curators:
                  initialValues.curators && initialValues.curators.length > 0
                      ? initialValues.curators
                      : [{ user: user?.documentId, percent: 3.5 }],
              contacts:
                  initialValues.contacts && initialValues.contacts.length > 0
                      ? initialValues.contacts.map(contact => ({
                            ...contact,
                            noAddress: contact.noAddress ?? !contact.address?.trim(),
                            workingHours:
                                typeof contact.workingHours === 'string'
                                    ? parseWorkingHours(contact.workingHours)
                                    : Array.isArray(contact.workingHours)
                                      ? contact.workingHours
                                      : undefined,
                        }))
                      : [{ noAddress: false }],
          }
        : {
              defermentInDays: 0,
              noLegalAddress: false,
              curators: [{ user: user?.documentId, percent: 3.5 }],
              contacts: [{ noAddress: false }],
          }

    const handleFinish = (values: ClientFormValues) => {
        const normalizedValues: ClientFormValues = {
            ...values,
            contacts: values.contacts?.map(contact => ({
                ...contact,
                workingHours:
                    Array.isArray(contact.workingHours) && contact.workingHours.length === 2
                        ? formatWorkingHours(contact.workingHours as [Dayjs, Dayjs])
                        : contact.workingHours,
            })),
        }
        onFinish(normalizedValues)
    }

    const onClick = async () => {
        try {
            await form.validateFields()

            const values: ClientFormValues = form.getFieldsValue()

            const normalizedValues: ClientFormValues = {
                ...values,
                contacts: values.contacts?.map(contact => ({
                    ...contact,
                    workingHours:
                        Array.isArray(contact.workingHours) && contact.workingHours.length === 2
                            ? formatWorkingHours(contact.workingHours as [Dayjs, Dayjs])
                            : contact.workingHours,
                })),
            }
            onFinish(normalizedValues)
        } catch (error) {
            console.error(error)
        }
    }

    return (
        <Form
            form={form}
            layout='vertical'
            initialValues={normalizedInitialValues}
            autoComplete='off'
            // onFinish={handleFinish}
        >
            <Row gutter={24}>
                <Col xs={24} sm={6}>
                    <Form.Item
                        label='Имя клиента'
                        name='name'
                        rules={[
                            { required: true, message: 'Введите имя клиента', whitespace: true },
                        ]}
                    >
                        <Input placeholder='Введите имя клиента' disabled={disabled} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                    <Form.Item label='Статус подтверждения' name='approvalStatus'>
                        <Select
                            placeholder='Выберите статус'
                            disabled={disabled || !isLogist}
                            options={[
                                { value: 'draft' as ApprovalStatus, label: 'Черновик' },
                                { value: 'approved' as ApprovalStatus, label: 'Подтвержден' },
                            ]}
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={6}>
                    <AddressSuggest
                        name='legalAddress'
                        noAddressName='noLegalAddress'
                        absoluteNoAddressPath='noLegalAddress'
                        label='Юридический адрес'
                        disabled={disabled}
                        onNoAddressChange={checked => {
                            if (checked) {
                                form.setFieldValue('legalAddress', undefined)
                            }
                        }}
                    />
                </Col>

                <Col xs={24} sm={6}>
                    <Form.Item label='Отсрочка (в днях)' name='defermentInDays'>
                        <InputNumber
                            placeholder='Введите отсрочку в днях'
                            disabled={disabled}
                            min={0}
                            precision={0}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                    <Form.Item label='Источник' name='source'>
                        <Select
                            placeholder='Выберите источник'
                            options={[
                                { value: 'site' as ClientSource, label: 'Сайт' },
                                { value: 'exhibition' as ClientSource, label: 'Выставка' },
                                { value: 'manager' as ClientSource, label: 'Менеджер' },
                            ]}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                    <Form.Item label='Наименование выставки' name='exhibitionName'>
                        <Input placeholder='Введите наименование выставки' />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                    <Form.Item label='Комментарий' name='comment'>
                        <Input.TextArea
                            placeholder='Введите комментарий'
                            disabled={disabled}
                            rows={4}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <div style={{ marginBottom: 32 }}>
                <div style={{ fontWeight: 500, marginBottom: 16 }}>Кураторы</div>
                <CuratorsField disabled={disabled} />
            </div>

            <div style={{ marginTop: 8 }}>
                <div style={{ fontWeight: 500, marginBottom: 16 }}>Контакты</div>
                <Form.List
                    name='contacts'
                    rules={[
                        {
                            validator: async (_, contacts) => {
                                if (!contacts || contacts.length === 0) {
                                    return Promise.reject(
                                        new Error('Добавьте минимум один контакт')
                                    )
                                }

                                const hasPrimary = contacts.some(
                                    (contact: ContactFormValues) => contact?.isPrimary
                                )
                                if (!hasPrimary) {
                                    return Promise.reject(
                                        new Error('Укажите хотя бы один основной контакт')
                                    )
                                }

                                return Promise.resolve()
                            },
                        },
                    ]}
                >
                    {(fields, { add, remove }, meta) => (
                        <>
                            <Row gutter={[24, 24]}>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Col xs={24} sm={6} key={key}>
                                        <div
                                            style={{
                                                border: '1px solid #d9d9d9',
                                                borderRadius: 6,
                                                padding: 16,
                                            }}
                                        >
                                            <Flex
                                                style={{ marginBottom: 8 }}
                                                justify='space-between'
                                                align='center'
                                            >
                                                <span style={{ fontWeight: 500 }}>
                                                    Контакт {name + 1}
                                                </span>
                                                <MinusCircleOutlined
                                                    onClick={() => remove(name)}
                                                    style={{
                                                        color: '#ff4d4f',
                                                        cursor: disabled
                                                            ? 'not-allowed'
                                                            : 'pointer',
                                                    }}
                                                    disabled={disabled}
                                                />
                                            </Flex>

                                            <AddressSuggest
                                                name={[name, 'address']}
                                                noAddressName={[name, 'noAddress']}
                                                absoluteNoAddressPath={[
                                                    'contacts',
                                                    name,
                                                    'noAddress',
                                                ]}
                                                label='Адрес получателя'
                                                disabled={disabled}
                                                onSelectSuggestion={suggestion => {
                                                    form.setFieldValue(
                                                        ['contacts', name, 'latCoordinates'],
                                                        suggestion.data.geo_lat != null
                                                            ? Number(suggestion.data.geo_lat)
                                                            : undefined
                                                    )
                                                    form.setFieldValue(
                                                        ['contacts', name, 'longCoordinates'],
                                                        suggestion.data.geo_lon != null
                                                            ? Number(suggestion.data.geo_lon)
                                                            : undefined
                                                    )
                                                }}
                                                onClearSuggestion={() => {
                                                    form.setFieldValue(
                                                        ['contacts', name, 'latCoordinates'],
                                                        undefined
                                                    )
                                                    form.setFieldValue(
                                                        ['contacts', name, 'longCoordinates'],
                                                        undefined
                                                    )
                                                }}
                                                onNoAddressChange={checked => {
                                                    if (checked) {
                                                        form.setFieldValue(
                                                            ['contacts', name, 'address'],
                                                            undefined
                                                        )
                                                        form.setFieldValue(
                                                            ['contacts', name, 'latCoordinates'],
                                                            undefined
                                                        )
                                                        form.setFieldValue(
                                                            ['contacts', name, 'longCoordinates'],
                                                            undefined
                                                        )
                                                    }
                                                }}
                                            />

                                            <Form.Item
                                                {...restField}
                                                label='Наименование точки'
                                                name={[name, 'pointName']}
                                            >
                                                <Input
                                                    placeholder='Наименование точки'
                                                    disabled={disabled}
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                {...restField}
                                                label='Имя получателя'
                                                name={[name, 'receiverName']}
                                                rules={requiredRules}
                                            >
                                                <Input
                                                    placeholder='Имя получателя'
                                                    disabled={disabled}
                                                />
                                            </Form.Item>

                                            <Phone
                                                {...restField}
                                                namePath={[name]}
                                                rules={[...requiredRules, ...phoneRules]}
                                                disabled={disabled}
                                                placeholder='+7 (999) 999-99-99'
                                                label='Телефон получателя'
                                            />

                                            <Form.Item
                                                {...restField}
                                                label='Часы работы'
                                                name={[name, 'workingHours']}
                                            >
                                                <TimePicker.RangePicker
                                                    format='HH:mm'
                                                    placeholder={['Начало', 'Конец']}
                                                    disabled={disabled}
                                                    style={{ width: '100%' }}
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                {...restField}
                                                label='Комментарий к контакту'
                                                name={[name, 'comment']}
                                            >
                                                <Input.TextArea
                                                    placeholder='Комментарий к контакту'
                                                    disabled={disabled}
                                                    rows={3}
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                {...restField}
                                                label='Широта'
                                                name={[name, 'latCoordinates']}
                                            >
                                                <InputNumber
                                                    placeholder='Широта'
                                                    disabled={true}
                                                    // step={0.000001}
                                                    style={{ width: '100%' }}
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                {...restField}
                                                label='Долгота'
                                                name={[name, 'longCoordinates']}
                                            >
                                                <InputNumber
                                                    placeholder='Долгота'
                                                    disabled={true}
                                                    // step={0.000001}
                                                    style={{ width: '100%' }}
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                {...restField}
                                                label='Основной контакт'
                                                name={[name, 'isPrimary']}
                                                valuePropName='checked'
                                            >
                                                <Switch disabled={disabled} />
                                            </Form.Item>

                                            <Form.Item name={[name, 'documentId']} hidden>
                                                <Input type='hidden' />
                                            </Form.Item>
                                        </div>
                                    </Col>
                                ))}
                                <Col xs={24} sm={6}>
                                    <Form.Item>
                                        <Button
                                            type='dashed'
                                            onClick={() => add({ noAddress: false })}
                                            icon={<PlusOutlined />}
                                            block
                                            disabled={disabled}
                                        >
                                            Добавить контакт
                                        </Button>
                                    </Form.Item>
                                </Col>
                            </Row>
                            {meta.errors.map(error => (
                                <Alert style={{ marginTop: 16 }} message={error} type='error' />
                            ))}
                        </>
                    )}
                </Form.List>
            </div>
            <br />
            <Form.Item>
                <Button
                    type='primary'
                    htmlType='submit'
                    loading={loading}
                    disabled={disabled}
                    onClick={onClick}
                >
                    {submitText}
                </Button>
            </Form.Item>
        </Form>
    )
}

export default ClientForm
